// Global packages and functions
import { useMemo } from 'react'

// Types
interface Props {
  totalCount: number
  pageSize: number
  siblingCount?: number
  activePage: number
}

// "More" dots
export const LEFT_DOTS = 'LEFT_DOTS'
export const RIGHT_DOTS = 'RIGHT_DOTS'

// Work out the number of pages
const range = (start: number, end: number) => {
  const length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

// Main export
const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  activePage,
}: Props) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize)

    // Pages count is determined as siblingCount + firstPage + lastPage + activePage + 2*DOTS
    const totalPageNumbers = siblingCount + 5

    // If the number of pages is less than the page numbers we want to show in our
    // paginationComponent, we return the range [1..totalPageCount]
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount)
    }

    const leftSiblingIndex = Math.max(activePage - siblingCount, 1)
    const rightSiblingIndex = Math.min(
      activePage + siblingCount,
      totalPageCount
    )

    // We do not want to show dots if there is only one position left
    // after/before the left/right page count as that would lead to a change if our Pagination
    // component size which we do not want
    const showLeftdots = leftSiblingIndex > 2
    const showRightDots = rightSiblingIndex < totalPageCount - 2

    const firstPageIndex = 1
    const lastPageIndex = totalPageCount

    if (!showLeftdots && showRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(1, leftItemCount)

      return [...leftRange, RIGHT_DOTS, totalPageCount]
    }

    if (showLeftdots && !showRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      )
      return [firstPageIndex, LEFT_DOTS, ...rightRange]
    }

    if (showLeftdots && showRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [
        firstPageIndex,
        LEFT_DOTS,
        ...middleRange,
        RIGHT_DOTS,
        lastPageIndex,
      ]
    }
  }, [totalCount, pageSize, siblingCount, activePage])

  return paginationRange
}

export default usePagination
